import type { ComponentType, ReactNode } from 'react';
import Dialog from '@/components/Dialog';
import DefaultDialogActions, {
  type DefaultDialogActionsProps,
} from '@/components/Dialog/DefaultDialogActions';
import {
  CircleHelpIcon,
  CircleInfoIcon,
  CircleXIcon,
  DoneIcon,
  TriangleAlertIcon,
  type BaseIconProps,
} from '@/icons';
import type { DialogVariant } from './types';
import { classes, resolveAccent, resolveTone, styles } from './utils';

export type { DialogVariant } from './types';

export interface DefaultDialogProps extends Omit<
  DefaultDialogActionsProps,
  'className' | 'tone' | 'fullWidth'
> {
  disableEscapeKeyDown?: boolean;
  disableBackdropClose?: boolean;
  variant?: DialogVariant;
  icon?: ReactNode;
  title: ReactNode;
  message: ReactNode;
  onClose: () => void;
}

const VARIANT_ICONS: Record<DialogVariant, ComponentType<BaseIconProps>> = {
  info: CircleInfoIcon,
  success: DoneIcon,
  warning: TriangleAlertIcon,
  error: CircleXIcon,
  confirm: CircleHelpIcon,
};

export default function DefaultDialog({
  disableEscapeKeyDown,
  disableBackdropClose,
  variant = 'confirm',
  destructive,
  icon,
  title,
  message,
  onClose,
  ...actionProps
}: DefaultDialogProps) {
  const isDestructive = destructive ?? variant === 'confirm';
  const accent = resolveAccent(variant, isDestructive);
  const tone = resolveTone(variant, isDestructive);

  const Glyph = VARIANT_ICONS[variant];
  const badgeIcon = icon ?? <Glyph size="1.375rem" />;

  return (
    <Dialog
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableBackdropClose={disableBackdropClose}
      className={classes.body}
      paperProps={{ className: classes.paper }}
      onClose={onClose}>
      <div className={classes.header}>
        <div style={styles.badge(accent)} className={classes.badge}>
          {badgeIcon}
        </div>
        <div className={classes.textBlock}>
          <div className={classes.title}>{title}</div>
          <div className={classes.message}>{message}</div>
        </div>
      </div>
      <DefaultDialogActions
        {...actionProps}
        fullWidth
        destructive={isDestructive}
        className={classes.actions}
        tone={tone}
        onCancel={actionProps.onCancel}
      />
    </Dialog>
  );
}
